import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import {
  CreateVendorConfigPayload,
  VendorAssetType,
  VendorConfig,
} from "../../../../types/onboarding";
import { assetFileName } from "../../../../utils/vendorConfigs";

interface VendorConfigDialogProps {
  visible: boolean;
  // null = adding a new manufacturer.
  config: VendorConfig | null;
  onHide: () => void;
  onSubmit: (payload: CreateVendorConfigPayload) => Promise<boolean>;
  onUpload: (
    vendorId: string,
    assetType: VendorAssetType,
    file: File
  ) => Promise<boolean>;
}

const ASSETS: { type: VendorAssetType; label: string; hint: string }[] = [
  { type: "PDF_GUIDE", label: "Setup guide", hint: "Attached to the customer's configuration email." },
  { type: "CERTIFICATE", label: "Security certificate", hint: "Attached when this manufacturer needs one." },
  { type: "EMAIL_TEMPLATE", label: "Email template", hint: "Used to compose the configuration email." },
];

const storedKey = (
  config: VendorConfig | null,
  type: VendorAssetType
): string | null => {
  if (!config) return null;
  if (type === "PDF_GUIDE") return config.pdf_guide_s3_key;
  if (type === "CERTIFICATE") return config.certificate_s3_key;
  return config.email_template_s3_key;
};

/**
 * Add or edit one manufacturer configuration, and replace its files.
 *
 * Two things are deliberately absent: there is no way to clear the certificate
 * format and no way to remove a file. The API merges partial updates and treats
 * a null as "leave unchanged", so either control would silently do nothing.
 */
const VendorConfigDialog: React.FC<VendorConfigDialogProps> = ({
  visible,
  config,
  onHide,
  onSubmit,
  onUpload,
}) => {
  const isEdit = !!config;
  const [vendorId, setVendorId] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [requiresGuide, setRequiresGuide] = useState(false);
  const [requiresCertificate, setRequiresCertificate] = useState(false);
  const [certificateType, setCertificateType] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState<VendorAssetType | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (!visible) return;
    setVendorId(config?.vendor_id ?? "");
    setVendorName(config?.vendor_name ?? "");
    setRequiresGuide(config?.requires_pdf_guide ?? false);
    setRequiresCertificate(config?.requires_certificate ?? false);
    setCertificateType(config?.certificate_type ?? "");
    setError(null);
    setSubmitting(false);
  }, [visible, config]);

  const submit = async () => {
    const id = vendorId.trim().toUpperCase();
    const name = vendorName.trim();
    if (!id) {
      setError("Please enter the manufacturer's identifier.");
      return;
    }
    if (!/^[A-Z0-9_]+$/.test(id)) {
      setError("The identifier may only contain letters, numbers and underscores.");
      return;
    }
    if (!name) {
      setError("Please enter the name shown to the team.");
      return;
    }
    setSubmitting(true);
    try {
      const ok = await onSubmit({
        vendor_id: id,
        vendor_name: name,
        requires_pdf_guide: requiresGuide,
        requires_certificate: requiresCertificate,
        certificate_type: certificateType.trim() || null,
      });
      if (ok) onHide();
    } finally {
      setSubmitting(false);
    }
  };

  const pickFile = async (type: VendorAssetType, file?: File | null) => {
    if (!file || !config) return;
    setUploading(type);
    try {
      await onUpload(config.vendor_id, type, file);
    } finally {
      setUploading(null);
      const input = fileInputs.current[type];
      if (input) input.value = "";
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button
        label={isEdit ? "Save" : "Add manufacturer"}
        icon="pi pi-check"
        onClick={submit}
        loading={submitting}
        disabled={submitting}
      />
    </div>
  );

  return (
    <Dialog
      header={isEdit ? `Edit ${config?.vendor_name}` : "Add manufacturer"}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "40rem" }}
    >
      {error && <Message severity="error" className="w-full mb-3" text={error} />}

      <div className="field p-fluid mb-3">
        <label htmlFor="vendor-id" className="block mb-1">
          Identifier
        </label>
        <InputText
          id="vendor-id"
          value={vendorId}
          disabled={isEdit}
          onChange={(e) => {
            setVendorId(e.target.value);
            setError(null);
          }}
          placeholder="ALFEN"
          aria-label="Identifier"
        />
        <small className="text-500">
          {isEdit
            ? "The identifier cannot be changed."
            : "Must match the manufacturer name used on the prospect, e.g. ALFEN."}
        </small>
      </div>

      <div className="field p-fluid mb-3">
        <label htmlFor="vendor-name" className="block mb-1">
          Name
        </label>
        <InputText
          id="vendor-name"
          value={vendorName}
          onChange={(e) => {
            setVendorName(e.target.value);
            setError(null);
          }}
          placeholder="Alfen"
          aria-label="Name"
        />
      </div>

      <div className="field mb-2 flex align-items-center gap-2">
        <Checkbox
          inputId="requires-guide"
          checked={requiresGuide}
          onChange={(e) => setRequiresGuide(!!e.checked)}
        />
        <label htmlFor="requires-guide">Customers need a setup guide</label>
      </div>

      <div className="field mb-3 flex align-items-center gap-2">
        <Checkbox
          inputId="requires-certificate"
          checked={requiresCertificate}
          onChange={(e) => setRequiresCertificate(!!e.checked)}
        />
        <label htmlFor="requires-certificate">
          Customers need a security certificate
        </label>
      </div>

      {requiresCertificate && (
        <div className="field p-fluid mb-3">
          <label htmlFor="certificate-type" className="block mb-1">
            Certificate file type
          </label>
          <InputText
            id="certificate-type"
            value={certificateType}
            onChange={(e) => setCertificateType(e.target.value)}
            placeholder=".cer"
            aria-label="Certificate file type"
          />
          <small className="text-500">
            Whatever this manufacturer expects — for example .cer or .fwi.
          </small>
        </div>
      )}

      {isEdit && (
        <div className="mt-4">
          <h4 className="mt-0 mb-2">Files</h4>
          {ASSETS.map(({ type, label, hint }) => {
            const name = assetFileName(storedKey(config, type));
            return (
              <div
                key={type}
                className="flex flex-wrap align-items-center justify-content-between gap-2 py-2 border-bottom-1 surface-border"
              >
                <div>
                  <div className="font-medium">{label}</div>
                  <div className="text-500 text-sm">{hint}</div>
                </div>
                <div className="flex align-items-center gap-2">
                  {name ? (
                    <Tag severity="success" value={name} />
                  ) : (
                    <Tag severity="warning" value="Not attached" />
                  )}
                  <Button
                    label={name ? "Replace" : "Upload"}
                    icon="pi pi-upload"
                    text
                    size="small"
                    loading={uploading === type}
                    disabled={!!uploading}
                    onClick={() => fileInputs.current[type]?.click()}
                  />
                  <input
                    ref={(el) => {
                      fileInputs.current[type] = el;
                    }}
                    type="file"
                    className="hidden"
                    aria-label={`${label} file`}
                    onChange={(e) => pickFile(type, e.target.files?.[0])}
                  />
                </div>
              </div>
            );
          })}
          <small className="text-500 block mt-2">
            Uploading replaces the current file. Files cannot be removed, only
            replaced.
          </small>
        </div>
      )}
    </Dialog>
  );
};

export default VendorConfigDialog;
